<template>
  <div>
    <b-card>
      <b-card-header>
        <div class="d-flex justify-content-between w-100 align-items-center">
          <h5>نماذج وإفادات التقارير</h5>
          <b-button
            variant="primary"
            @click="openModal('create')"
          >
            إضافة تقرير جديد
          </b-button>
        </div>
      </b-card-header>

      <b-card-body>
        <!-- Data Table -->
        <b-table
          :items="dynamicDataReports"
          :fields="fields"
          :busy="isBusy"
          show-empty
          empty-text="لا توجد بيانات"
          responsive
        >
          <template #table-busy>
            <div class="text-center text-danger my-2">
              <b-spinner class="align-middle" />
              <strong>جاري التحميل...</strong>
            </div>
          </template>

          <template #cell(logo)="row">
            <img
              v-if="row.item.logo && (row.item.logo.url || row.item.logo.path)"
              :src="row.item.logo.url || row.item.logo.path"
              alt="logo"
              height="40"
              class="rounded shadow-sm"
            >
            <span
              v-else
              class="text-muted"
            >لا يوجد</span>
          </template>

          <template #cell(document_purpose)="row">
            <span :title="row.item.document_purpose">
              {{ truncateText(row.item.document_purpose, 60) }}
            </span>
          </template>

          <template #cell(actions)="row">
            <b-button
              variant="outline-info"
              size="sm"
              class="mr-1"
              @click="openViewModal(row.item)"
            >
              عرض
            </b-button>
            <b-button
              variant="outline-primary"
              size="sm"
              class="mr-1"
              @click="openModal('edit', row.item)"
            >
              تعديل
            </b-button>
            <b-button
              variant="outline-danger"
              size="sm"
              @click="confirmDelete(row.item)"
            >
              حذف
            </b-button>
          </template>
        </b-table>
      </b-card-body>
    </b-card>

    <!-- View Modal -->
    <b-modal
      v-model="showViewModal"
      title="تفاصيل بيانات التقرير"
      size="lg"
      hide-footer
    >
      <div v-if="selectedReport">
        <dl class="row mb-0">
          <dt class="col-sm-4">اسم الشركة :</dt>
          <dd class="col-sm-8">{{ selectedReport.company_name }}</dd>

          <dt class="col-sm-4">اللقب / العنوان :</dt>
          <dd class="col-sm-8">{{ selectedReport.company_subtitle }}</dd>

          <dt class="col-sm-4">الشهادة :</dt>
          <dd class="col-sm-8">{{ selectedReport.company_accreditation }}</dd>

          <dt class="col-sm-4">التواصل :</dt>
          <dd class="col-sm-8">{{ selectedReport.company_contact }}</dd>

          <dt class="col-sm-4">العنوان :</dt>
          <dd class="col-sm-8">{{ selectedReport.company_address }}</dd>

          <dt class="col-sm-4">الهاتف :</dt>
          <dd class="col-sm-8">{{ selectedReport.company_phone }}</dd>

          <dt class="col-sm-4">البريد الإلكتروني :</dt>
          <dd class="col-sm-8">{{ selectedReport.company_email }}</dd>

          <dt class="col-sm-4">عنوان التقرير :</dt>
          <dd class="col-sm-8">{{ selectedReport.document_title }}</dd>

          <dt class="col-sm-4">العنوان الفرعي للتقرير :</dt>
          <dd class="col-sm-8">{{ selectedReport.document_subtitle }}</dd>

          <dt class="col-sm-4">وصف التقرير :</dt>
          <dd class="col-sm-8">{{ selectedReport.document_description }}</dd>

          <dt class="col-sm-4">التوقيعات :</dt>
          <dd class="col-sm-8">
            <div
              v-for="signature in getReportSignatures(selectedReport)"
              :key="`view-signature-${signature.order}-${signature.name}`"
              class="mb-1"
            >
              {{ signature.order }} - {{ signature.title || signature.subject }} : {{ signature.name }}
            </div>
          </dd>

          <dt class="col-sm-4">تاريخ التوقيع :</dt>
          <dd class="col-sm-8">{{ selectedReport.company_manager_signature_date }}</dd>

          <dt class="col-sm-4 mb-0">صيغة الإفادة :</dt>
          <dd
            class="col-sm-8 mb-0"
            style="white-space: pre-wrap;"
          >{{ selectedReport.document_purpose }}</dd>
        </dl>

        <div
          v-if="selectedReport.logo && (selectedReport.logo.url || selectedReport.logo.path)"
          class="mt-3 border-top pt-3 text-center"
        >
          <p class="font-weight-bold">الشعار</p>
          <img
            :src="selectedReport.logo.url || selectedReport.logo.path"
            alt="Logo"
            class="img-fluid rounded"
            style="max-height: 150px;"
          >
        </div>
        <div
          v-if="selectedReport.main_logo && (selectedReport.main_logo.url || selectedReport.main_logo.path)"
          class="mt-3 text-center"
        >
          <p class="font-weight-bold">الشعار الرئيسي</p>
          <img
            :src="selectedReport.main_logo.url || selectedReport.main_logo.path"
            alt="Main Logo"
            class="img-fluid rounded"
            style="max-height: 150px;"
          >
        </div>
      </div>
      <div class="d-flex justify-content-end mt-3 border-top pt-3">
        <b-button
          variant="secondary"
          @click="showViewModal = false"
        >إغلاق</b-button>
      </div>
    </b-modal>

    <!-- Create / Edit Modal -->
    <b-modal
      v-model="showEditCompanyModal"
      :title="isEditMode ? 'تعديل بيانات التقرير' : 'إضافة تقرير جديد'"
      size="lg"
      hide-footer
      no-close-on-backdrop
    >
      <b-form @submit.prevent="submitCompanyData">
        <validation-observer
          ref="modelForm"
          class="w-100"
        >
          <b-row>
            <b-col
              md="6"
              sm="12"
            >
              <validation-provider
                #default="{ errors }"
                name="logo"
                vid="logo"
                rules=""
              >
                <b-form-group label="الشعار">
                  <b-form-file
                    v-model="editCompany.logo"
                    accept="image/*"
                    @change="previewLogo"
                  />
                  <img
                    v-if="logoPreview"
                    :src="logoPreview"
                    alt="logo"
                    class="img-fluid mt-2 rounded shadow-sm"
                    style="max-height: 100px;"
                  >
                  <small class="text-danger d-block mt-1">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col
              md="6"
              sm="12"
            >
              <b-form-group label="الشعار الرئيسي">
                <b-form-file
                  v-model="editCompany.main_logo"
                  accept="image/jpeg,image/png,image/jpg,image/gif,image/svg+xml,image/svg"
                  @change="previewMainLogo"
                />
                <img
                  v-if="mainLogoPreview"
                  :src="mainLogoPreview"
                  alt="main-logo"
                  class="img-fluid mt-2 rounded shadow-sm"
                  style="max-height: 100px;"
                >
                <small
                  v-if="mainLogoError"
                  class="text-danger d-block mt-1"
                >{{ mainLogoError }}</small>
              </b-form-group>
            </b-col>
            <b-col
              md="6"
              sm="12"
            >
              <validation-provider
                #default="{ errors }"
                name="company_name"
                vid="company_name"
                rules="required"
              >
                <b-form-group label="اسم الشركة">
                  <b-form-input v-model="editCompany.company_name" />
                  <small class="text-danger d-block mt-1">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col
              md="6"
              sm="12"
            >
              <validation-provider
                #default="{ errors }"
                name="company_subtitle"
                vid="company_subtitle"
                rules="required"
              >
                <b-form-group label="العنوان / اللقب">
                  <b-form-input v-model="editCompany.company_subtitle" />
                  <small class="text-danger d-block mt-1">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col
              md="6"
              sm="12"
            >
              <validation-provider
                #default="{ errors }"
                name="company_accreditation"
                vid="company_accreditation"
                rules="required"
              >
                <b-form-group label="الشهادة">
                  <b-form-input v-model="editCompany.company_accreditation" />
                  <small class="text-danger d-block mt-1">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col
              md="6"
              sm="12"
            >
              <validation-provider
                #default="{ errors }"
                name="company_contact"
                vid="company_contact"
                rules="required"
              >
                <b-form-group label="التواصل">
                  <b-form-input v-model="editCompany.company_contact" />
                  <small class="text-danger d-block mt-1">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col
              md="6"
              sm="12"
            >
              <validation-provider
                #default="{ errors }"
                name="company_address"
                vid="company_address"
                rules="required"
              >
                <b-form-group label="العنوان">
                  <b-form-input v-model="editCompany.company_address" />
                  <small class="text-danger d-block mt-1">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col
              md="6"
              sm="12"
            >
              <validation-provider
                #default="{ errors }"
                name="company_phone"
                vid="company_phone"
                rules="required"
              >
                <b-form-group label="الهاتف">
                  <b-form-input v-model="editCompany.company_phone" />
                  <small class="text-danger d-block mt-1">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col
              md="6"
              sm="12"
            >
              <validation-provider
                #default="{ errors }"
                name="company_email"
                vid="company_email"
                rules="required|email"
              >
                <b-form-group label="البريد الإلكتروني">
                  <b-form-input
                    v-model="editCompany.company_email"
                    type="email"
                  />
                  <small class="text-danger d-block mt-1">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col
              md="6"
              sm="12"
            >
              <validation-provider
                #default="{ errors }"
                name="document_title"
                vid="document_title"
                rules="required"
              >
                <b-form-group label="عنوان التقرير">
                  <b-form-input v-model="editCompany.document_title" />
                  <small class="text-danger d-block mt-1">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col
              md="6"
              sm="12"
            >
              <b-form-group label="العنوان الفرعي للتقرير">
                <b-form-input v-model="editCompany.document_subtitle" />
              </b-form-group>
            </b-col>
            <b-col
              md="12"
              sm="12"
            >
              <b-form-group label="وصف التقرير">
                <b-form-textarea v-model="editCompany.document_description" />
              </b-form-group>
            </b-col>
            <b-col
              md="12"
              sm="12"
            >
              <b-form-group label="التوقيعات">
                <div class="signature-compact-head d-none d-md-flex">
                  <div class="signature-idx">#</div>
                  <div class="signature-title">المسمى</div>
                  <div class="signature-name">الاسم</div>
                  <div class="signature-actions">التحكم</div>
                </div>
                <div
                  v-for="(signature, index) in editCompany.signatures"
                  :key="`signature-row-${index}`"
                  class="signature-compact-row"
                >
                  <div class="signature-idx">
                    <span class="signature-order-badge">{{ index + 1 }}</span>
                  </div>
                  <div class="signature-title">
                    <b-form-input
                      v-model="signature.title"
                      size="sm"
                      :placeholder="`المسمى ${index + 1}`"
                      @input="clearSignatureError(index, 'title')"
                    />
                    <small class="text-danger d-block mt-1">{{ getSignatureError(index, 'title') }}</small>
                  </div>
                  <div class="signature-name">
                    <b-form-input
                      v-model="signature.name"
                      size="sm"
                      :placeholder="`الاسم ${index + 1}`"
                      @input="clearSignatureError(index, 'name')"
                    />
                    <small class="text-danger d-block mt-1">{{ getSignatureError(index, 'name') }}</small>
                  </div>
                  <div class="signature-actions">
                    <b-button
                      size="sm"
                      variant="outline-secondary"
                      class="compact-action-btn"
                      :disabled="index === 0"
                      @click="moveSignatureUp(index)"
                    >
                      ↑
                    </b-button>
                    <b-button
                      size="sm"
                      variant="outline-secondary"
                      class="compact-action-btn"
                      :disabled="index === editCompany.signatures.length - 1"
                      @click="moveSignatureDown(index)"
                    >
                      ↓
                    </b-button>
                    <b-button
                      size="sm"
                      variant="outline-danger"
                      class="compact-action-btn"
                      :disabled="editCompany.signatures.length === 1"
                      @click="removeSignature(index)"
                    >
                      ×
                    </b-button>
                  </div>
                </div>
                <small class="text-danger d-block mt-1">{{ signatureListError }}</small>
                <b-button
                  size="sm"
                  variant="outline-primary"
                  @click="addSignature()"
                >
                  إضافة توقيع
                </b-button>
              </b-form-group>
            </b-col>
            <b-col
              md="6"
              sm="12"
            >
              <b-form-group label="تاريخ توقيع المدير">
                <b-form-input
                  v-model="editCompany.company_manager_signature_date"
                  type="date"
                />
              </b-form-group>
            </b-col>
            <b-col
              md="12"
              sm="12"
            >
              <validation-provider
                #default="{ errors }"
                name="document_purpose"
                vid="document_purpose"
                rules="required"
              >
                <b-form-group label="صيغة الإفادة (document_purpose)">
                  <b-form-textarea
                    v-model="editCompany.document_purpose"
                    rows="4"
                  />
                  <small class="text-danger d-block mt-1">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>
        </validation-observer>
      </b-form>
      <div class="d-flex justify-content-end mt-3 border-top pt-3">
        <b-button
          variant="primary"
          class="mr-1"
          @click="submitCompanyData()"
        >حفظ</b-button>
        <b-button
          variant="secondary"
          @click="closeEditCompanyModal()"
        >إلغاء</b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BCardHeader, BCardBody, BButton, BTable, BSpinner, BRow, BCol, BFormInput, BModal, BForm, BFormGroup, BFormTextarea, BFormFile
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required, email } from '@validations'
import { mapGetters } from 'vuex'
import Settings from '@/api/system/setting'
import request from '@/utils/request'

const settings = new Settings()

export default {
  name: 'StatementFormulations',
  components: {
    BCard, BCardHeader, BCardBody, BButton, BTable, BSpinner, BRow, BCol, BFormInput, BModal, BForm, BFormGroup, BFormTextarea, BFormFile, ValidationProvider, ValidationObserver
  },
  data() {
    return {
      required,
      email,
      
      // Table configuration
      fields: [
        { key: 'logo', label: 'الشعار' },
        { key: 'company_name', label: 'اسم الشركة', sortable: true },
        { key: 'document_title', label: 'عنوان التقرير' },
        { key: 'document_purpose', label: 'صيغة الإفادة' },
        { key: 'actions', label: 'الإجراءات', class: 'text-center', thStyle: { width: '220px' } }
      ],
      
      // Modal State
      showEditCompanyModal: false,
      showViewModal: false,
      isEditMode: false,
      selectedReport: null,
      logoPreview: null,
      mainLogoPreview: null,
      mainLogoError: '',
      
      // Form State
      editCompany: {
        id: null,
        logo: null,
        logo_id: null,
        main_logo: null,
        main_logo_id: null,
        company_name: 'شركـــــة الحلوانـــــــــي',
        company_subtitle: 'للاعمال المساحية المتكاملة',
        company_accreditation: 'معتمدة من الهيئة المصرية العامة للمساحة',
        company_contact: 'alhalawany-esga.com | 0882740740-01020011222',
        company_address: 'اسيوط - مركز الفتح - طريق الفتح ابنوب العمومي - بحري بنزينة موبيل - بجوار كافيه طاولة',
        company_phone: '0882740740-01020011222',
        company_email: 'alhalwanyc@gmail.com',
        document_title: 'بيان مساحي',
        document_subtitle: 'لإستخراج بيان صلاحية موقع للبناء',
        document_description: 'تم تنفيذ أعمال المساحة بالموقع دون الرجوع لمستندات الملكية و ذلك بناءً على إرشاد المواطن ووصفه أثناء المعاينة وبدون اعتراض من أحد، و هذا البيان وصفي دقيق و لا يعتبر دليل على الملكية و يستخدم فقط للغرض المحدد والمطلوب.',
        company_manager_name: 'مهندس مدني / احمد عبد الموجود صديق محمد',
        company_manager_signature: 'مدير عام الشركة',
        signatures: [
          {
            title: 'مدير عام الشركة',
            name: 'مهندس مدني / احمد عبد الموجود صديق محمد',
            order: 1,
          }
        ],
        company_manager_signature_date: '',
        document_purpose: 'لزوم استخراج بيان صلاحية موقع للبناء طبقا لقانون البناء الصادر برقم 119 لسنة 2008',
      },
      signatureErrors: [],
      signatureListError: '',
    }
  },
  computed: {
    ...mapGetters({
      dynamicDataReports: 'settings/dynamicDataReports',
      isBusy: 'settings/loadDynamicDataReport',
    }),
  },
  mounted() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      try {
        await this.$store.dispatch('settings/dynamicDataReports')
      } catch (error) {
        console.error('Error fetching data:', error)
      }
    },
    
    truncateText(text, length) {
      if (!text) return ''
      return text.length > length ? text.substring(0, length) + '...' : text
    },

    openViewModal(item) {
      this.selectedReport = {
        ...item,
        signatures: this.normalizeSignatures(item.signatures, item),
      }
      this.showViewModal = true
    },

    openModal(mode, item = null) {
      if (mode === 'edit' && item) {
        this.isEditMode = true
        this.editCompany = {
          id: item.id,
          logo_id: item.logo_id || null,
          company_name: item.company_name || '',
          company_subtitle: item.company_subtitle || '',
          company_accreditation: item.company_accreditation || '',
          company_contact: item.company_contact || '',
          company_address: item.company_address || '',
          company_phone: item.company_phone || '',
          company_email: item.company_email || '',
          document_title: item.document_title || '',
          document_subtitle: item.document_subtitle || '',
          document_description: item.document_description || '',
          company_manager_name: item.company_manager_name || '',
          company_manager_signature: item.company_manager_signature || '',
          signatures: this.normalizeSignatures(item.signatures, item),
          company_manager_signature_date: item.company_manager_signature_date || '',
          document_purpose: item.document_purpose || '',
          logo: null,
          main_logo: null,
          main_logo_id: item.main_logo_id || null,
        }
        if (item.logo) {
          this.logoPreview = item.logo.path || item.logo.url || item.logo.name;
        } else {
          this.logoPreview = null;
        }
        if (item.main_logo) {
          this.mainLogoPreview = item.main_logo.path || item.main_logo.url || item.main_logo.name
        } else {
          this.mainLogoPreview = null
        }
      } else {
        this.isEditMode = false
        this.editCompany = {
          id: null,
          logo: null,
          logo_id: null,
          main_logo: null,
          main_logo_id: null,
          company_name: 'شركـــــة الحلوانـــــــــي',
          company_subtitle: 'للاعمال المساحية المتكاملة',
          company_accreditation: 'معتمدة من الهيئة المصرية العامة للمساحة',
          company_contact: 'alhalawany-esga.com | 0882740740-01020011222',
          company_address: 'اسيوط - مركز الفتح - طريق الفتح ابنوب العمومي - بحري بنزينة موبيل - بجوار كافيه طاولة',
          company_phone: '0882740740-01020011222',
          company_email: 'alhalwanyc@gmail.com',
          document_title: 'بيان مساحي',
          document_subtitle: 'لإستخراج بيان صلاحية موقع للبناء',
          document_description: 'تم تنفيذ أعمال المساحة بالموقع دون الرجوع لمستندات الملكية و ذلك بناءً على إرشاد المواطن ووصفه أثناء المعاينة وبدون اعتراض من أحد، و هذا البيان وصفي دقيق و لا يعتبر دليل على الملكية و يستخدم فقط للغرض المحدد والمطلوب.',
          company_manager_name: 'مهندس مدني / احمد عبد الموجود صديق محمد',
          company_manager_signature: 'مدير عام الشركة',
          signatures: [
            {
              title: 'مدير عام الشركة',
              name: 'مهندس مدني / احمد عبد الموجود صديق محمد',
              order: 1,
            }
          ],
          company_manager_signature_date: '',
          document_purpose: 'لزوم استخراج بيان صلاحية موقع للبناء طبقا لقانون البناء الصادر برقم 119 لسنة 2008',
        }
        this.logoPreview = null
        this.mainLogoPreview = null
      }
      
      // Reset form validation if possible
      if (this.$refs.modelForm) {
        this.$refs.modelForm.reset()
      }
      
      this.signatureErrors = []
      this.signatureListError = ''
      this.mainLogoError = ''
      this.showEditCompanyModal = true
    },
    
    closeEditCompanyModal() {
      this.showEditCompanyModal = false
      this.signatureErrors = []
      this.signatureListError = ''
      this.mainLogoError = ''
    },
    
    previewLogo(event) {
      if (!event.target.files) return;
      const file = event.target.files[0]
      if (file) {
        this.editCompany.logo = file
        this.logoPreview = URL.createObjectURL(file)
      }
    },
    validateImageFile(file, fieldLabel = 'الصورة') {
      if (!file) return ''
      const allowedMimeTypes = [
        'image/jpeg',
        'image/png',
        'image/jpg',
        'image/gif',
        'image/svg+xml',
        'image/svg',
      ]
      const maxSize = 2 * 1024 * 1024
      if (!allowedMimeTypes.includes(file.type)) {
        return `${fieldLabel}: نوع الملف غير مدعوم. المسموح: jpeg, jpg, png, gif, svg`
      }
      if (file.size > maxSize) {
        return `${fieldLabel}: الحد الأقصى لحجم الملف هو 2MB`
      }
      return ''
    },
    previewMainLogo(event) {
      const file = event?.target?.files ? event.target.files[0] : event
      if (!file) {
        this.editCompany.main_logo = null
        this.mainLogoError = ''
        return
      }
      const validationError = this.validateImageFile(file, 'الشعار الرئيسي')
      if (validationError) {
        this.mainLogoError = validationError
        this.editCompany.main_logo = null
        return
      }
      this.mainLogoError = ''
      this.editCompany.main_logo = file
      this.mainLogoPreview = URL.createObjectURL(file)
    },

    createSignatureRow(overrides = {}) {
      return {
        title: overrides.title || overrides.subject || '',
        subject: overrides.subject || overrides.title || '',
        name: overrides.name || '',
        order: Number.isInteger(overrides.order) && overrides.order > 0
          ? overrides.order
          : (this.editCompany.signatures?.length || 0) + 1,
      }
    },
    normalizeSignatures(signatures, fallbackReport = {}) {
      const source = Array.isArray(signatures) && signatures.length
        ? signatures
        : [{
          title: fallbackReport.company_manager_signature || '',
          subject: fallbackReport.company_manager_signature || '',
          name: fallbackReport.company_manager_name || '',
          order: 1,
        }]
      return source
        .map((signature, index) => ({
          ...this.createSignatureRow(signature),
          _index: index,
        }))
        .sort((a, b) => (a.order - b.order) || (a._index - b._index))
        .map((signature, index) => ({
          title: signature.title,
          subject: signature.subject || signature.title,
          name: signature.name,
          order: index + 1,
        }))
    },
    getReportSignatures(report) {
      return this.normalizeSignatures(report?.signatures, report || {})
    },
    getSignatureError(index, field) {
      return (this.signatureErrors[index] && this.signatureErrors[index][field]) || ''
    },
    clearSignatureError(index, field) {
      if (this.signatureErrors[index]) {
        this.$set(this.signatureErrors[index], field, '')
      }
      this.signatureListError = ''
    },
    addSignature() {
      this.editCompany.signatures.push(this.createSignatureRow())
      this.syncSignatureOrders()
      this.syncLegacyFieldsFromSignatures()
      this.signatureListError = ''
    },
    removeSignature(index) {
      if (this.editCompany.signatures.length === 1) return
      this.editCompany.signatures.splice(index, 1)
      this.syncSignatureOrders()
      this.syncLegacyFieldsFromSignatures()
    },
    moveSignatureUp(index) {
      if (index <= 0) return
      const current = this.editCompany.signatures[index]
      this.$set(this.editCompany.signatures, index, this.editCompany.signatures[index - 1])
      this.$set(this.editCompany.signatures, index - 1, current)
      this.syncSignatureOrders()
      this.syncLegacyFieldsFromSignatures()
    },
    moveSignatureDown(index) {
      if (index >= this.editCompany.signatures.length - 1) return
      const current = this.editCompany.signatures[index]
      this.$set(this.editCompany.signatures, index, this.editCompany.signatures[index + 1])
      this.$set(this.editCompany.signatures, index + 1, current)
      this.syncSignatureOrders()
      this.syncLegacyFieldsFromSignatures()
    },
    syncSignatureOrders() {
      this.editCompany.signatures = this.editCompany.signatures.map((signature, index) => ({
        ...signature,
        order: index + 1,
      }))
    },
    syncLegacyFieldsFromSignatures() {
      const firstSignature = this.editCompany.signatures[0] || {}
      this.editCompany.company_manager_signature = firstSignature.title || firstSignature.subject || ''
      this.editCompany.company_manager_name = firstSignature.name || ''
    },
    validateSignatures() {
      this.signatureErrors = []
      const signatures = this.editCompany.signatures || []
      if (!signatures.length) {
        this.signatureListError = 'يجب إضافة توقيع واحد على الأقل'
        return false
      }
      this.signatureListError = ''
      let isValid = true
      signatures.forEach((signature, index) => {
        const rowErrors = { title: '', name: '' }
        if (!(signature.title || signature.subject)) {
          rowErrors.title = 'العنوان مطلوب'
          isValid = false
        }
        this.$set(this.signatureErrors, index, rowErrors)
      })
      return isValid
    },
    appendSignaturesToFormData(formData, signatures) {
      signatures.forEach((signature, index) => {
        formData.append(`signatures[${index}][title]`, signature.title)
        formData.append(`signatures[${index}][subject]`, signature.subject || signature.title)
        formData.append(`signatures[${index}][name]`, signature.name)
        formData.append(`signatures[${index}][order]`, signature.order)
      })
    },
    
    async submitCompanyData() {
      const isValid = await this.$refs.modelForm.validate()
      if (!isValid) return
      if (this.mainLogoError) return
      this.syncSignatureOrders()
      this.syncLegacyFieldsFromSignatures()
      if (!this.validateSignatures()) return

      const formData = new FormData()
      Object.keys(this.editCompany).forEach(key => {
        if (
          this.editCompany[key] !== null
          && this.editCompany[key] !== undefined
          && key !== 'id'
          && key !== 'signatures'
          && key !== 'logo_id'
          && key !== 'main_logo_id'
        ) {
          formData.append(key, this.editCompany[key])
        }
      })
      this.appendSignaturesToFormData(formData, this.editCompany.signatures)

      try {
        if (this.isEditMode && this.editCompany.id) {
          await settings.updateDynamicDataReport(this.editCompany.id, formData)
          this.$toast.success('تم التحديث بنجاح')
        } else {
          await settings.storeDynamicDataReport(formData)
          this.$toast.success('تم الحفظ بنجاح')
        }
        this.closeEditCompanyModal()
        this.fetchData()
      } catch (error) {
        console.error('Error submitting data:', error)
        this.$toast.error('حدث خطأ أثناء الحفظ')
      }
    },

    confirmDelete(item) {
      this.$swal({
        title: 'هل أنت متأكد؟',
        text: 'لن تتمكن من التراجع عن هذا!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'نعم، احذف!',
        cancelButtonText: 'إلغاء',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          // DELETE via API using request fallback since deleteDynamicDataReport might not be in settings class
          request.delete(`/settings/reports/${item.id}`).then(() => {
            this.$toast.success('تم الحذف بنجاح')
            this.fetchData()
          }).catch(err => {
            console.error(err)
            this.$toast.error('حدث خطأ أثناء الحذف')
          })
        }
      })
    }
  }
}
</script>

<style scoped>
textarea {
  resize: vertical;
}
dt {
  margin-bottom: 12px;
  font-weight: 600;
}
dd {
  margin-bottom: 12px;
  color: #495057;
}
.signature-compact-head {
  font-size: 12px;
  font-weight: 600;
  color: #6c757d;
  margin-bottom: 6px;
  padding: 0 4px;
}
.signature-compact-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 8px;
  margin-bottom: 8px;
  background: #fafbfc;
}
.signature-idx {
  width: 40px;
  flex: 0 0 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.signature-order-badge {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #e9f2ff;
  color: #2f6fd6;
  font-weight: 600;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.signature-title,
.signature-name {
  flex: 1;
  min-width: 0;
}
.signature-actions {
  width: 106px;
  flex: 0 0 106px;
  display: flex;
  justify-content: flex-end;
  gap: 4px;
  padding-top: 1px;
}
.compact-action-btn {
  width: 30px;
  padding: 0;
  line-height: 1.1;
}
@media (max-width: 767px) {
  .signature-compact-row {
    flex-wrap: wrap;
  }
  .signature-idx {
    width: 100%;
    justify-content: flex-start;
  }
  .signature-title,
  .signature-name,
  .signature-actions {
    width: 100%;
    flex: 0 0 100%;
  }
  .signature-actions {
    justify-content: flex-start;
  }
}
</style>
